import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ShoppingCart } from 'lucide-react';
import { Button } from '@/components/common/Button';
import { Modal } from '@/components/common/Modal';
import { TextField } from '@/components/forms/Field';
import { DataTable, type Column } from '@/components/tables/DataTable';
import { FilterSelect, ListPage } from '@/components/tables/ListPage';
import { useToast } from '@/components/feedback/ToastProvider';
import { useApiErrorHandler } from '@/hooks/useApiErrorHandler';
import { useTableQuery } from '@/hooks/useTableQuery';
import { acquisitionsApi, librariesApi } from '@/services/api/endpoints/library';
import { queryKeys } from '@/services/api/queryKeys';
import { useAuth } from '@/features/auth/useAuth';
import { PERMISSIONS } from '@/types/permissions';
import { ROUTES } from '@/app/router/routes';
import type { Acquisition, AcquisitionStatus } from '@/types/library';
import { formatDate, orDash } from '@/utils/format';
import { AcquisitionStatusBadge, Money, humanise, today } from './shared';

const STATUSES: { value: AcquisitionStatus; label: string }[] = [
  { value: 'DRAFT', label: 'Draft' },
  { value: 'ORDERED', label: 'Placed' },
  { value: 'PARTIALLY_RECEIVED', label: 'Part delivered' },
  { value: 'RECEIVED', label: 'Delivered' },
  { value: 'CANCELLED', label: 'Cancelled' },
];

/**
 * Books being bought.
 *
 * <p>An order is not stock. Copies are accessioned when a delivery is recorded,
 * and only for the quantities that actually arrived — ordering thirty and
 * receiving twenty-eight has to leave the catalogue saying twenty-eight, or the
 * first stock count reports two phantom copies missing.
 *
 * <p>Part deliveries are the normal case, so an order stays open until every
 * line is filled. Whether the rest is still coming or never coming is a decision
 * for whoever is chasing the supplier, not something to infer from one delivery
 * note.
 */
export function AcquisitionsPage() {
  const { can } = useAuth();
  const toast = useToast();
  const handleApiError = useApiErrorHandler();
  const queryClient = useQueryClient();
  const table = useTableQuery('orderDate,desc');
  const manages = can(PERMISSIONS.ACQUISITION_MANAGE);

  const [status, setStatus] = useState<AcquisitionStatus | ''>('');
  const [creating, setCreating] = useState(false);
  const [supplierId, setSupplierId] = useState('');
  const [source, setSource] = useState<'PURCHASE' | 'DONATION' | 'TRANSFER'>('PURCHASE');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [fundingSource, setFundingSource] = useState('');
  const [orderDate, setOrderDate] = useState(today());

  const params = { ...table.params, ...(status === '' ? {} : { status: [status] }) };

  const acquisitions = useQuery({
    queryKey: queryKeys.acquisitions.list(params),
    queryFn: () => acquisitionsApi.list(params),
  });

  const suppliers = useQuery({
    queryKey: queryKeys.acquisitions.suppliers(true),
    queryFn: () => acquisitionsApi.suppliers(true),
    enabled: creating,
  });

  const libraries = useQuery({
    queryKey: queryKeys.library.list,
    queryFn: () => librariesApi.list(),
    enabled: creating,
  });

  const create = useMutation({
    mutationFn: () =>
      acquisitionsApi.create({
        libraryId: libraries.data?.[0]?.id,
        supplierId: supplierId === '' ? null : supplierId,
        source,
        invoiceNumber: invoiceNumber.trim() === '' ? undefined : invoiceNumber.trim(),
        fundingSource: fundingSource.trim() === '' ? undefined : fundingSource.trim(),
        orderDate,
      }),
    onSuccess: (acquisition) => {
      toast.success(`${acquisition.reference} created — add the titles to it`);
      setCreating(false);
      setInvoiceNumber('');
      setFundingSource('');
      void queryClient.invalidateQueries({ queryKey: queryKeys.acquisitions.all });
    },
    onError: (error) => handleApiError(error),
  });

  const columns: Column<Acquisition>[] = [
    {
      key: 'reference',
      header: 'Order',
      render: (row) => (
        <span>
          <Link
            to={ROUTES.acquisitionDetail(row.id)}
            className="block font-mono text-[12px] text-ink underline-offset-2 hover:text-accent hover:underline"
          >
            {row.reference}
          </Link>
          <span className="block text-[11px] text-subtle">{formatDate(row.orderDate)}</span>
        </span>
      ),
    },
    {
      key: 'supplier',
      header: 'From',
      render: (row) => (
        <span>
          <span className="block text-sm text-ink">
            {orDash(row.supplierName ?? (row.source === 'PURCHASE' ? undefined : humanise(row.source)))}
          </span>
          <span className="block text-[11px] text-subtle">{orDash(row.invoiceNumber)}</span>
        </span>
      ),
    },
    { key: 'source', header: 'How', render: (row) => humanise(row.source) },
    {
      key: 'copies',
      header: 'Copies',
      className: 'text-right',
      render: (row) => {
        const ordered = row.items.reduce((sum, item) => sum + item.quantity, 0);
        const received = row.items.reduce((sum, item) => sum + item.receivedQuantity, 0);
        return (
          <span>
            <span className="block text-sm text-ink">
              {received} of {ordered}
            </span>
            {received > 0 && received < ordered && (
              <span className="block text-[11px] text-[color:var(--sms-warning)]">
                {ordered - received} still owed
              </span>
            )}
          </span>
        );
      },
    },
    {
      key: 'totalCost',
      header: 'Cost',
      sortKey: 'totalCost',
      className: 'text-right',
      render: (row) => <Money value={row.totalCost} />,
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => <AcquisitionStatusBadge status={row.status} />,
    },
  ];

  return (
    <>
      <ListPage
        title="Acquisitions"
        description="Books on order, and what has arrived."
        icon={<ShoppingCart className="h-5 w-5" aria-hidden="true" />}
        action={manages ? <Button onClick={() => setCreating(true)}>Raise an order</Button> : undefined}
        filters={
          <FilterSelect
            label="Status"
            value={status}
            onChange={(value) => {
              setStatus(value);
              table.setPage(0);
            }}
            options={STATUSES}
          />
        }
        pagination={acquisitions.data?.pagination}
        onPageChange={table.setPage}
        onSizeChange={table.setSize}
      >
        <DataTable
          columns={columns}
          rows={acquisitions.data?.data}
          rowKey={(row) => row.id}
          loading={acquisitions.isPending}
          error={acquisitions.error}
          onRetry={() => void acquisitions.refetch()}
          sort={table.sort}
          onSort={table.toggleSort}
          emptyTitle="Nothing on order"
          emptyDescription="Raise an order to record books being bought, donated or transferred in."
        />
      </ListPage>

      <Modal
        open={creating}
        onClose={() => setCreating(false)}
        title="Raise an order"
        description="Starts as a draft. No copies exist until a delivery is recorded."
        footer={
          <>
            <Button variant="secondary" onClick={() => setCreating(false)}>
              Cancel
            </Button>
            <Button onClick={() => create.mutate()} disabled={create.isPending}>
              Create it
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <label className="block">
            <span className="text-xs font-semibold uppercase tracking-[0.04em] text-muted">
              How the books are coming
            </span>
            <select
              className="mt-1 w-full border border-line bg-bg px-3 py-2 text-sm text-ink"
              value={source}
              onChange={(event) =>
                setSource(event.target.value as 'PURCHASE' | 'DONATION' | 'TRANSFER')
              }
            >
              <option value="PURCHASE">Bought</option>
              <option value="DONATION">Donated</option>
              <option value="TRANSFER">Transferred in</option>
            </select>
          </label>
          <label className="block">
            <span className="text-xs font-semibold uppercase tracking-[0.04em] text-muted">
              Supplier
            </span>
            <select
              className="mt-1 w-full border border-line bg-bg px-3 py-2 text-sm text-ink"
              value={supplierId}
              onChange={(event) => setSupplierId(event.target.value)}
            >
              <option value="">None — a donation or a transfer</option>
              {(suppliers.data ?? []).map((row) => (
                <option key={row.id} value={row.id}>
                  {row.name}
                </option>
              ))}
            </select>
          </label>
          <TextField
            label="Order date"
            type="date"
            value={orderDate}
            onChange={(event) => setOrderDate(event.target.value)}
          />
          <TextField
            label="Invoice number"
            value={invoiceNumber}
            onChange={(event) => setInvoiceNumber(event.target.value)}
          />
          <TextField
            label="Which budget"
            value={fundingSource}
            onChange={(event) => setFundingSource(event.target.value)}
          />
        </div>
      </Modal>
    </>
  );
}
